import { Field, useFormikContext } from 'formik';
import React, { FC, useState, useEffect } from 'react';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import { Button } from '../button/Button';
import { ButtonTypes } from '../../models/ButtonTypes';
import { useGlobalState } from '../../Providers';
import { savePassword } from '../../services/controllerService';
import { getErrorMessage } from '../../services/utilities/ServiceError';
import { requiredValidation, validateIpAddress } from '../../utilities/Validation';
import { DialogContent } from '../dialog/Dialog';
import { useDialog } from '../dialog/DialogContext';
import Label from '../label/label';
import SettingSelectField from '../settingFields/SettingSelectField';
import Togglebox from '../togglebox/togglebox';
import { toast } from 'react-toastify';
import {NETWORK_MODE} from "@/constants/network-constants.ts";

interface ControllerNetworkProps {
    isPasswordModalOpen: boolean;
    setIsPasswordModalOpen: (open: boolean) => void;
}

export const ControllerNetwork: FC<ControllerNetworkProps> = ({ isPasswordModalOpen, setIsPasswordModalOpen }) => {
    const { translate } = useLocale();
    const GlobalContext = useGlobalState();
    const { errors, values, setFieldValue } = useFormikContext<any>();
    const [isOpen, setIsOpen] = useState(false);
    const dialog = useDialog();
    const isTlsEnabled = GlobalContext?.buildInfo.tlsMode === NETWORK_MODE.CLOUD;

    const handleOnChanged = () => {
        if (!!!values.safeIp) {
            const dialogContent: DialogContent = {
                title: translate.byKey(TranslationKeys.enableSecureMode),
                message: translate.byKey(TranslationKeys.secureModeMessage),
                buttons: [
                    {
                        id: "btn_secureMode",
                        onClick: () => { setFieldValue('safeIp', !values.safeIp) },
                        text: translate.byKey(TranslationKeys.enable),
                        type: ButtonTypes.BlueRoundedButton,
                        className: "text-base font-medium"
                    },
                    {
                        id: "btn_Cancel",
                        onClick: () => { }, // Empty onClick will just close the dialog
                        text: translate.byKey(TranslationKeys.cancel),
                        type: ButtonTypes.TextButton,
                        className: "justify-center px-4 py-1 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm"
                    }
                ]
            }
            dialog.pushInfo(dialogContent);
        }
        else {
            setFieldValue('safeIp', false)
        }
    }

    const onChangePassword = (passwordValues) => {
        savePassword(passwordValues.newPassword, values.discoveryId, passwordValues.currentPassword)
            .then((response) => {
                toast.success(translate.byKey(TranslationKeys.passwordChangedSuccessfully));
                setIsOpen(false)
            }).catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
            }).finally(() => {
                setIsOpen(false);
            })
    }

    return <>
        <div className="flex flex-row">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='hostname'
                    className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.hostname)}:</Label>
                <Field
                    name="hostname"
                    type="text"
                    validate={(value) => requiredValidation(value, translate)}
                    disabled={isTlsEnabled}
                    className={`flex flex-row items-center pr-2 pl-2 p-1 ${isTlsEnabled ? ' text-gray-500 cursor-not-allowed' : 'text-gray-800'} text-sm bg-gray-input w-full`} />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='wanIpAddress'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.wanIpAddress)}:</Label>
                <Field
                    name="wanIpAddress"
                    type="text"
                    disabled={true}
                    className={`flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 cursor-not-allowed text-sm bg-gray-input w-full`} />
            </div>
        </div>
        <div className="flex flex-row pt-2">
            <div className="relative flex flex-col flex-1 pr-3">
                <Label htmlFor='ipAddress'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.ipAddress)}:
                    {errors.ipAddress ? <span className='absolute right-0 mr-4 text-red-600 text-xs'>{errors.ipAddress}</span> : null}
                    </Label>
                <Field
                    name="ipAddress"
                    type="text"
                    validate={(value) => validateIpAddress(value, translate, true)}
                    disabled={!!!values.staticIpAddress || isTlsEnabled}
                    className={`flex flex-row items-center pr-2 pl-2 p-1 ${!!!values.staticIpAddress || isTlsEnabled ? ' text-gray-500 cursor-not-allowed' : 'text-gray-800'}  text-sm bg-gray-input w-full`} />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='staticIpAddress'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.staticIpAddress)}:</Label>
                <div className="my-0">
                    <Togglebox name="staticIpAddress" label="" disabled={isTlsEnabled} />
                </div>
            </div>
        </div>
        <div className="flex flex-row pt-2">
            <div className="relative flex flex-col flex-1 pr-3">
                <Label htmlFor='subnetMask'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.subnetMask)}:</Label>
                    {errors.subnetMask ? <span className='absolute right-0 mr-4 text-red-600 text-xs'>{errors.subnetMask}</span> : null}
                <Field
                    name="subnetMask"
                    type="text"
                    validate={(value) => validateIpAddress(value, translate, true)}
                    disabled={!!!values.staticIpAddress || isTlsEnabled}
                    className={`flex flex-row items-center pr-2 pl-2 p-1 ${!!!values.staticIpAddress ? ' text-gray-500 cursor-not-allowed' : 'text-gray-800'}  text-sm bg-gray-input w-full`} />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='safeIp'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.secureMode)}:</Label>
                <div className="my-0">
                    <Togglebox name="safeIp" label="" onChanged={handleOnChanged} disabled={isTlsEnabled} />
                </div>
            </div>
        </div>
        <div className="flex flex-row pt-2">
            <div className="relative flex flex-col flex-1 pr-3">
                <Label htmlFor='gateway'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.gateway)}:</Label>
                    {errors.gateway ? <span className='absolute right-0 mr-4 text-red-600 text-xs'>{errors.gateway}</span> : null}
                <Field
                    name="gateway"
                    validate={(value) => validateIpAddress(value, translate, true)}
                    disabled={!!!values.staticIpAddress || isTlsEnabled}
                    className={`flex flex-row items-center pr-2 pl-2 p-1 ${!!!values.staticIpAddress ? ' text-gray-500 cursor-not-allowed' : 'text-gray-800'}  text-sm bg-gray-input w-full`} />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='forceChanges'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.forceChanges)}:</Label>
                <div className="my-0">
                    <Togglebox name="forceChanges" label="" disabled={isTlsEnabled} />
                </div>
            </div>
        </div>
        <div className="flex flex-row pt-2">
            <div className="flex flex-col flex-1 pr-3">
                <SettingSelectField
                    className=''
                    key={'updateTypeSelectField'}
                    accessor={"updateType"}
                    displayName={translate.byKey(TranslationKeys.updateType)}
                    disabled={isTlsEnabled}
                    options={[
                        { value: '0', label: translate.byKey(TranslationKeys.updateDeviceOnly) },
                        { value: '1', label: translate.byKey(TranslationKeys.updateDeviceAndCommsChannel) }
                    ]}>
                </SettingSelectField>
            </div>
            <div className="flex flex-col flex-1 pr-3">
                {/* Placeholder to give extra empty column */}
            </div>
        </div>
        <div className="flex flex-row pt-3">
            <span className="text-portal-blue-primary text-xl font-bold bound-title">{translate.byKey(TranslationKeys.ports)}</span>
        </div>
        <div className="flex flex-row pt-2">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='incomingPortFromEngine'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.incomingPortFromEngine)}:</Label>
                <Field
                    name="incomingPortFromEngine"
                    type="number"
                    className={`flex flex-row items-center pr-2 pl-2 p-1 ${isTlsEnabled ? ' text-gray-500 cursor-not-allowed' : 'text-gray-800'} text-sm bg-gray-input w-full`}
                    disabled={isTlsEnabled} />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='outgoingPortFromEngine'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.outgoingPortFromEngine)}:</Label>
                <Field
                    name="outgoingPortFromEngine"
                    type="number"
                    className={`flex flex-row items-center pr-2 pl-2 p-1 ${isTlsEnabled ? ' text-gray-500 cursor-not-allowed' : 'text-gray-800'} text-sm bg-gray-input w-full`}
                    disabled={isTlsEnabled}/>
            </div>
        </div>
        <div className="flex flex-row pt-2">
            <div className="flex flex-col flex-1 pr-3">
                <Label htmlFor='incomingPortFromDC'
                    className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.incomingPortFromDC)}:</Label>
                <Field
                    name="incomingPortFromDC"
                    type="number"
                    className={`flex flex-row items-center pr-2 pl-2 p-1 ${isTlsEnabled ? ' text-gray-500 cursor-not-allowed' : 'text-gray-800'} text-sm bg-gray-input w-full`}
                    disabled={isTlsEnabled} />
            </div>
            <div className="flex flex-col flex-1 pr-3">
                {/* Placeholder to give extra empty column */}
            </div>
        </div>
        <div className="flex flex-row pt-3">
            <span className="text-portal-blue-primary text-xl font-bold bound-title">{translate.byKey(TranslationKeys.advanced)}</span>
        </div>
        <div className="flex flex-row pt-3">
            <Button variant="primary" shape="rounded" onClick={() => setIsPasswordModalOpen(true)}>{translate.byKey(TranslationKeys.changePassword)}</Button>
        </div>
    </>
}
